import type {
  Lead,
  LeadEventWithStatusDto,
} from '@up2stay/up2promo-api-client';
import { LeadEventLifecycleStatus } from '@up2stay/up2promo-api-client';

import {
  collectDistinctLeadTags,
  LEAD_FILTER_UNASSIGNED_ID,
  leadNormalizedAssigneeFilterId,
} from '@/core/components/leads/lead-filter-normalize';
import {
  buildEventNameMap,
  eventNamesFromLead,
} from '@/core/lib/lead-event-names';

export function buildAssigneeFilterOptions(options: {
  leads: Lead[];
  memberLabelByUserId: Map<string, string>;
  unknownAuthorLabel: string;
  unassignedLabel: string;
  collatorLocale: string;
}): Array<{ userId: string; label: string }> {
  const {
    leads,
    memberLabelByUserId,
    unknownAuthorLabel,
    unassignedLabel,
    collatorLocale,
  } = options;
  const ids = new Set<string>();
  for (const lead of leads) {
    ids.add(leadNormalizedAssigneeFilterId(lead));
  }
  const opts = [...ids].map((userId) => ({
    userId,
    label:
      userId === LEAD_FILTER_UNASSIGNED_ID
        ? unassignedLabel
        : (memberLabelByUserId.get(userId) ?? unknownAuthorLabel),
  }));
  opts.sort((a, b) => {
    if (a.userId === LEAD_FILTER_UNASSIGNED_ID) return -1;
    if (b.userId === LEAD_FILTER_UNASSIGNED_ID) return 1;
    return a.label.localeCompare(b.label, collatorLocale, {
      sensitivity: 'base',
    });
  });
  return opts;
}

export function buildEventFilterOptions(options: {
  eventsEnabled: boolean;
  leadEvents: LeadEventWithStatusDto[];
  collatorLocale: string;
  eventStatusLabels: Record<string, string> | undefined;
}): Array<{ id: string; label: string }> | null {
  const { eventsEnabled, leadEvents, collatorLocale, eventStatusLabels } =
    options;
  if (!eventsEnabled) return null;
  const statusOrder: Record<string, number> = {
    [LeadEventLifecycleStatus.ACTIVE]: 0,
    [LeadEventLifecycleStatus.UPCOMING]: 1,
    [LeadEventLifecycleStatus.PAST]: 2,
  };
  const sorted = [...leadEvents].sort((a, b) => {
    const sa = statusOrder[a.status] ?? 99;
    const sb = statusOrder[b.status] ?? 99;
    if (sa !== sb) return sa - sb;
    return a.name.localeCompare(b.name, collatorLocale, {
      sensitivity: 'base',
    });
  });
  return sorted.map((ev) => {
    const statusLabel =
      eventStatusLabels?.[ev.status] ?? String(ev.status ?? '');
    return {
      id: ev.id,
      label: statusLabel ? `${ev.name} (${statusLabel})` : ev.name,
    };
  });
}

export function buildLeadEventMaps(options: {
  eventsEnabled: boolean;
  leadEvents: LeadEventWithStatusDto[];
  leads: Lead[];
  collatorLocale: string;
}) {
  const { eventsEnabled, leadEvents, leads, collatorLocale } = options;

  const eventAutoTagById = new Map<string, string>();
  for (const ev of leadEvents) {
    if (ev.autoTag?.trim()) eventAutoTagById.set(ev.id, ev.autoTag.trim());
  }

  const extra: Record<string, string> = {};
  for (const item of leads) {
    Object.assign(extra, eventNamesFromLead(item));
  }
  const eventNameById = buildEventNameMap(leadEvents, extra);

  const legacyEventTagExclusions = !eventsEnabled
    ? ([] as string[])
    : [
        ...new Set(
          [...eventAutoTagById.values(), ...eventNameById.values()]
            .map((t) => t.trim())
            .filter(Boolean),
        ),
      ];

  const excludeTagKeys = new Set(
    legacyEventTagExclusions.map((t) => t.toLowerCase()),
  );

  const tagFilterOptions = collectDistinctLeadTags(
    leads,
    collatorLocale,
    legacyEventTagExclusions,
  );

  return {
    eventNameById,
    leadCardLabelMaps: { eventNameById, excludeTagKeys },
    tagFilterOptions,
    legacyEventTagExclusions,
  };
}
